import { createUserMessage } from '@/ai/utils/promptUtils'

export const chatLogType = {
  USER_PROMPT: 'user-prompt',
  AI_TEXT_RESPONSE: 'ai-text-response',
  AI_FUNCTION_CALL: 'ai-function-call'
}

// ~15k tokens of conversation. The workflow spec is re-sent in full every turn, so evicted
// history costs conversational context only — never state.
export const HISTORY_MAX_CHARS = 60000

const OMITTED_NOTICE = '[Earlier messages in this conversation were omitted to stay within the model\'s context limit.]'

const rowSize = (message) => (message.content?.length || 0) + (message.aiOutput?.length || 0)

// Returns the index to replay from. A cut may only land on a user prompt: a function_call
// replayed without its function_call_output is rejected by every provider.
const findStartIndex = (messages, maxChars) => {
  let total = 0
  let start = messages.length

  for (let index = messages.length - 1; index >= 0; index--) {
    total += rowSize(messages[index])

    if (total > maxChars) {
      break
    }

    if (messages[index].type === chatLogType.USER_PROMPT) {
      start = index
    }
  }

  return start
}

export const constructMessagesHistory = (messages, { maxChars = HISTORY_MAX_CHARS } = {}) => {
  const outputs = []
  const start = findStartIndex(messages, maxChars)

  if (start > 0) {
    outputs.push(createUserMessage(OMITTED_NOTICE))
  }

  for (const message of messages.slice(start)) {
    switch (message.type) {
      case chatLogType.USER_PROMPT:
        outputs.push(createUserMessage(message.content))
        break
      case chatLogType.AI_TEXT_RESPONSE:
        if (!message.aiOutput) {
          if (message.content) {
            outputs.push({
              type: 'message',
              role: 'assistant',
              content: [{ type: 'output_text', text: message.content }]
            })
          }
          continue
        }

        const output = JSON.parse(message.aiOutput).find((item) => item.type === 'message')
        outputs.push(output)
        break
      case chatLogType.AI_FUNCTION_CALL:
        if (!message.aiOutput) {
          continue
        }

        const parsedOutput = JSON.parse(message.aiOutput)

        const functionCallOutput = Array.isArray(parsedOutput) ? parsedOutput : parsedOutput.output
        const functionResults = Array.isArray(parsedOutput) ? [] : (parsedOutput.functionResults || [])

        for (const item of functionCallOutput) {
          if (item.type === 'function_call') {
            outputs.push(item)

            const storedResult = functionResults.find((r) => r.callId === item.call_id)
            outputs.push({
              type: 'function_call_output',
              call_id: item.call_id,
              output: JSON.stringify(storedResult?.result ?? { success: true })
            })
          }
        }

        if (message.content) {
          outputs.push({
            type: 'message',
            role: 'assistant',
            content: [{ type: 'output_text', text: message.content }]
          })
        }
        break
    }
  }

  return outputs
}
